import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  Link,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBackRounded as ArrowBackIcon,
  AutoAwesomeRounded as AutoAwesomeIcon,
  CloseRounded as CloseIcon,
  EditRounded as EditIcon,
  ExpandLessRounded as ExpandLessIcon,
  RefreshRounded as RefreshIcon,
  RestartAltRounded as RestartAltIcon,
  ShareRounded as ShareIcon,
  TouchAppRounded as TouchAppIcon,
  VisibilityOffRounded as VisibilityOffIcon,
  VisibilityRounded as VisibilityIcon,
  ZoomInRounded as ZoomInIcon,
  ZoomOutRounded as ZoomOutIcon,
} from '@mui/icons-material';
import { TransformComponent, TransformWrapper, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import type { MasterToolSearchFilters, SuperShowcase, SuperShowcaseHotspot } from '../../services/api';
import {
  describeHotspotAction,
  getHotspotColor,
  getProjectResearchConfig,
  getShowcaseActionMeta,
  summarizeProjectResearchFilters,
} from './showcaseActions';
import FreehandPolygonOverlay, { normalizePathRings } from './FreehandPolygonOverlay';
import ShowcaseShareDialog, { ShowcaseShareMethod } from './ShowcaseShareDialog';

function freehandBounds(pathData: SuperShowcaseHotspot['pathData']) {
  const points = normalizePathRings(pathData).flat();
  if (!points.length) return null;
  const minX = Math.min(...points.map((point) => point.x));
  const maxX = Math.max(...points.map((point) => point.x));
  const minY = Math.min(...points.map((point) => point.y));
  const maxY = Math.max(...points.map((point) => point.y));
  return { x: minX, y: minY, width: Math.max(maxX - minX, 0.001), height: Math.max(maxY - minY, 0.001) };
}

/**
 * Fit an image into a viewport while preserving aspect ratio. Used so the
 * hotspot overlay box matches the painted pixels (no letterboxing).
 */
function containSize(
  naturalWidth: number,
  naturalHeight: number,
  maxWidth: number,
  maxHeight: number,
): { width: number; height: number } {
  if (naturalWidth <= 0 || naturalHeight <= 0 || maxWidth <= 0 || maxHeight <= 0) {
    return { width: Math.max(0, maxWidth), height: Math.max(0, maxHeight) };
  }
  const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight);
  return {
    width: Math.max(1, Math.round(naturalWidth * scale)),
    height: Math.max(1, Math.round(naturalHeight * scale)),
  };
}

/**
 * Super Showcase (agent view): a gallery of admin-curated images that are
 * clickable. Picking an image opens an immersive viewer where a light trails
 * around every mapped area; clicking one hands off to the Super Picker via
 * `onHotspotAction` (Price Promo, Thumbsup, a pre-filled Project Research
 * search, or a hyperlink that opens in a new tab).
 */

/**
 * Keyframes for the hotspot signifiers. Declared once for the whole dialog —
 * they are colour-agnostic (position/opacity/scale only), so every hotspot
 * reuses them and tints itself through its own gradients.
 */
const SHOWCASE_KEYFRAMES = `
  @keyframes sscRailH {
    0%   { background-position: -90% 0; opacity: 0; }
    4%   { opacity: 1; }
    21%  { opacity: 1; }
    25%  { background-position: 190% 0; opacity: 0; }
    100% { background-position: 190% 0; opacity: 0; }
  }
  @keyframes sscRailV {
    0%   { background-position: 0 -90%; opacity: 0; }
    4%   { opacity: 1; }
    21%  { opacity: 1; }
    25%  { background-position: 0 190%; opacity: 0; }
    100% { background-position: 0 190%; opacity: 0; }
  }
  @keyframes sscBracket {
    0%, 100% { opacity: 0.7; }
    50%      { opacity: 1; }
  }
  @keyframes sscPing {
    0%   { transform: scale(0.82); opacity: 0.85; }
    70%  { transform: scale(1.26); opacity: 0; }
    100% { transform: scale(1.26); opacity: 0; }
  }
  @keyframes sscHalo {
    0%, 100% { opacity: 0.6; }
    50%      { opacity: 1; }
  }
  @keyframes sscLegendRail {
    0%   { background-position: -60% 0; }
    100% { background-position: 160% 0; }
  }
`;

/** Seconds for one lap of the trailing light; each rail owns a quarter of it. */
const RAIL_CYCLE_S = 3;

/**
 * The four perimeter rails. Bottom and left are mirrored so the light keeps
 * running clockwise instead of restarting from the same corner.
 */
const HOTSPOT_RAILS = [
  { key: 'top', axis: 'h', flip: 'none', box: { top: 0, left: 0, right: 0, height: 2 } },
  { key: 'right', axis: 'v', flip: 'none', box: { top: 0, bottom: 0, right: 0, width: 2 } },
  { key: 'bottom', axis: 'h', flip: 'scaleX(-1)', box: { bottom: 0, left: 0, right: 0, height: 2 } },
  { key: 'left', axis: 'v', flip: 'scaleY(-1)', box: { top: 0, bottom: 0, left: 0, width: 2 } },
] as const;

/**
 * Corner brackets — the perimeter is implied by these, never closed by a box.
 * `edge` names the two sides each bracket draws, filled in with the hotspot's
 * colour at render time.
 */
const HOTSPOT_CORNERS: {
  key: string;
  box: Record<string, number>;
  edge: (color: string) => Record<string, string>;
}[] = [
  {
    key: 'tl',
    box: { top: 0, left: 0, borderTopLeftRadius: 8 },
    edge: (color) => ({ borderTop: `2px solid ${color}`, borderLeft: `2px solid ${color}` }),
  },
  {
    key: 'tr',
    box: { top: 0, right: 0, borderTopRightRadius: 8 },
    edge: (color) => ({ borderTop: `2px solid ${color}`, borderRight: `2px solid ${color}` }),
  },
  {
    key: 'br',
    box: { bottom: 0, right: 0, borderBottomRightRadius: 8 },
    edge: (color) => ({ borderBottom: `2px solid ${color}`, borderRight: `2px solid ${color}` }),
  },
  {
    key: 'bl',
    box: { bottom: 0, left: 0, borderBottomLeftRadius: 8 },
    edge: (color) => ({ borderBottom: `2px solid ${color}`, borderLeft: `2px solid ${color}` }),
  },
];

/**
 * The interactive signifier for a mapped area — deliberately not a box. A
 * rectangle is drawn only by four corner brackets and four faint rails, with a
 * bright light trailing clockwise around them; an ellipse swaps the rails for a
 * radar ping. Nothing here fills the area, so the picture underneath stays
 * readable and the affordance comes from motion rather than from shading.
 */
const HotspotFrame: React.FC<{
  color: string;
  shape: SuperShowcaseHotspot['shape'];
  pathData?: SuperShowcaseHotspot['pathData'];
  /** Cards render the frame still; the viewer runs the trailing light. */
  live: boolean;
  hovered?: boolean;
  onClick?: React.MouseEventHandler<SVGSVGElement>;
  onMouseEnter?: React.MouseEventHandler<SVGSVGElement>;
  onMouseLeave?: React.MouseEventHandler<SVGSVGElement>;
}> = ({ color, shape, pathData, live, hovered = false, onClick, onMouseEnter, onMouseLeave }) => {
  if (shape === 'freehand') {
    return <FreehandPolygonOverlay pathData={pathData ?? []} color={color} animated={live} hovered={hovered} interactive={Boolean(onClick)} onClick={onClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} />;
  }
  if (shape === 'ellipse') {
    return (
      <>
        {/* A ring is the only way to read an ellipse, so it breathes rather
            than sitting there as a static outline. */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `1.5px solid ${color}`,
            opacity: hovered ? 1 : 0.7,
            filter: `drop-shadow(0 0 6px ${color}AA)`,
            animation: live ? 'sscHalo 2.6s ease-in-out infinite' : 'none',
            pointerEvents: 'none',
          }}
        />
        {live &&
          [0, 1].map((i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: `2px solid ${color}`,
                animation: `sscPing 2.6s cubic-bezier(0.4, 0, 0.6, 1) ${i * 1.3}s infinite`,
                pointerEvents: 'none',
              }}
            />
          ))}
      </>
    );
  }

  return (
    <>
      {HOTSPOT_RAILS.map((rail, i) => {
        const horizontal = rail.axis === 'h';
        return (
          <Box
            key={rail.key}
            sx={{
              position: 'absolute',
              ...rail.box,
              transform: rail.flip,
              pointerEvents: 'none',
              // Faded at the ends so the corners belong to the brackets and the
              // four rails never close into a rectangle. Kept dim: the rail is
              // the track, and the light running along it is what draws the eye.
              backgroundImage: `linear-gradient(${horizontal ? '90deg' : '180deg'}, ${color}00 0%, ${color}47 24%, ${color}47 76%, ${color}00 100%)`,
              opacity: hovered ? 1 : 0.85,
              ...(live && {
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  // Overhangs the 2px track so the head reads as a comet rather
                  // than as a brighter stretch of the same hairline.
                  inset: horizontal ? '-2px 0' : '0 -2px',
                  backgroundImage: `linear-gradient(${horizontal ? '90deg' : '180deg'}, transparent 0%, ${color}00 12%, ${color} 40%, #ffffff 50%, ${color} 60%, ${color}00 88%, transparent 100%)`,
                  backgroundSize: horizontal ? '42% 100%' : '100% 42%',
                  backgroundRepeat: 'no-repeat',
                  // Parked out of frame before the lap reaches this rail.
                  backgroundPosition: horizontal ? '-90% 0' : '0 -90%',
                  borderRadius: 3,
                  opacity: 0,
                  filter: `drop-shadow(0 0 9px ${color}) drop-shadow(0 0 3px #ffffff)`,
                  animation: `${horizontal ? 'sscRailH' : 'sscRailV'} ${RAIL_CYCLE_S}s linear ${(i * RAIL_CYCLE_S) / HOTSPOT_RAILS.length}s infinite`,
                },
              }),
            }}
          />
        );
      })}

      {HOTSPOT_CORNERS.map((corner, i) => (
        <Box
          key={corner.key}
          sx={{
            position: 'absolute',
            // Proportional on small areas, capped on big ones, so a bracket
            // can never grow past the corner it marks.
            width: 'min(18px, 22%)',
            height: 'min(18px, 22%)',
            pointerEvents: 'none',
            filter: `drop-shadow(0 0 5px ${color})`,
            animation: live ? `sscBracket 2.6s ease-in-out ${i * 0.2}s infinite` : 'none',
            ...corner.box,
            ...corner.edge(color),
          }}
        />
      ))}
    </>
  );
};

interface SuperShowcaseDialogProps {
  open: boolean;
  onClose: () => void;
  showcases: SuperShowcase[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  /** Fired when an agent clicks a mapped area — the app routes to the function. */
  onHotspotAction: (showcase: SuperShowcase, hotspot: SuperShowcaseHotspot) => void;
  /** Fired when an agent steps into a showcase image (analytics). */
  onShowcaseViewed?: (showcase: SuperShowcase) => void;
  /**
   * Renders the Project Research results card — the app passes its own, so the
   * table here is the same one (columns, headers, sorting, filters, export) as
   * on the Project Research tab.
   */
  renderProjectResearchResults?: () => React.ReactNode;
  /**
   * Renders the Project Research search criteria card, shown behind the
   * results' Edit link so the agent can refine the area's preset search.
   */
  renderProjectResearchCriteria?: () => React.ReactNode;
  /** The live search filters — the chips follow them once the agent edits. */
  projectResearchFilters?: MasterToolSearchFilters;
  /** Lets the app hide its inline copy of the results card while this shows it. */
  onProjectResearchOpenChange?: (open: boolean) => void;
  /** Open straight onto this showcase (share-link deep link), once. */
  initialShowcaseId?: number | null;
  onInitialShowcaseHandled?: () => void;
  /** Fired when an agent shares a showcase link (analytics). */
  onShowcaseShared?: (showcase: SuperShowcase, method: ShowcaseShareMethod, source: 'gallery' | 'viewer') => void;
}

const SuperShowcaseDialog: React.FC<SuperShowcaseDialogProps> = ({
  open,
  onClose,
  showcases,
  loading,
  error,
  onRefresh,
  onHotspotAction,
  onShowcaseViewed,
  renderProjectResearchResults,
  renderProjectResearchCriteria,
  projectResearchFilters,
  onProjectResearchOpenChange,
  initialShowcaseId = null,
  onInitialShowcaseHandled,
  onShowcaseShared,
}) => {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeId, setActiveId] = useState<number | null>(null);
  const [hoveredHotspotId, setHoveredHotspotId] = useState<number | null>(null);
  const [animatingHotspotId, setAnimatingHotspotId] = useState<number | null>(null);
  const hotspotAnimationTimerRef = useRef<number | null>(null);
  /** Set while a Project Research area's results are shown over the image. */
  const [resultsHotspot, setResultsHotspot] = useState<SuperShowcaseHotspot | null>(null);
  /** Whether the search criteria are open above the results. */
  const [editingCriteria, setEditingCriteria] = useState<boolean>(false);
  // Highlights start hidden so the picture is seen as-is; the header toggle
  // (and hovering a legend entry) brings the mapped areas up.
  const [showHotspots, setShowHotspots] = useState<boolean>(false);
  const [revealed, setRevealed] = useState<boolean>(false);
  const [hintVisible, setHintVisible] = useState<boolean>(true);
  /** Natural size of the open image, filled in on load when the DB has none. */
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);
  /** Pixel size of the overlay frame after contain-fit into the viewport. */
  const [frameSize, setFrameSize] = useState<{ width: number; height: number } | null>(null);
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const active = useMemo(
    () => showcases.find((showcase) => showcase.id === activeId) ?? null,
    [showcases, activeId],
  );

  const imageNatural = useMemo(() => {
    if (active?.imageWidth && active?.imageHeight) {
      return { width: active.imageWidth, height: active.imageHeight };
    }
    return naturalSize;
  }, [active?.imageWidth, active?.imageHeight, naturalSize]);

  // Reset to the gallery whenever the dialog is re-opened.
  useEffect(() => {
    if (open) return;
    setActiveId(null);
    setHoveredHotspotId(null);
    setAnimatingHotspotId(null);
    setResultsHotspot(null);
  }, [open]);

  useEffect(() => {
    if (hotspotAnimationTimerRef.current !== null) {
      window.clearTimeout(hotspotAnimationTimerRef.current);
      hotspotAnimationTimerRef.current = null;
    }
    setAnimatingHotspotId(null);
  }, [activeId, open]);

  useEffect(() => () => {
    if (hotspotAnimationTimerRef.current !== null) {
      window.clearTimeout(hotspotAnimationTimerRef.current);
    }
  }, []);

  // Stagger the hotspots in each time an image is opened, and let the
  // "tap a highlighted area" hint fade on its own.
  useEffect(() => {
    setNaturalSize(null);
    setFrameSize(null);
    if (!active) {
      setRevealed(false);
      setHintVisible(true);
      return;
    }
    setRevealed(false);
    setHintVisible(true);
    const revealTimer = window.setTimeout(() => setRevealed(true), 120);
    const hintTimer = window.setTimeout(() => setHintVisible(false), 6000);
    return () => {
      window.clearTimeout(revealTimer);
      window.clearTimeout(hintTimer);
    };
  }, [active]);

  useEffect(() => {
    onProjectResearchOpenChange?.(Boolean(resultsHotspot));
    setEditingCriteria(false);
  }, [resultsHotspot, onProjectResearchOpenChange]);

  // Size the overlay frame to the contained image so hotspot % match the picture.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !imageNatural) {
      setFrameSize(null);
      return;
    }
    const applySize = (width: number, height: number) => {
      if (width <= 0 || height <= 0) return;
      setFrameSize(containSize(imageNatural.width, imageNatural.height, width, height));
    };
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      applySize(entry.contentRect.width, entry.contentRect.height);
    });
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [imageNatural, active]);

  const handleActiveImageLoad = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
    const image = event.currentTarget;
    if (image.naturalWidth > 0 && image.naturalHeight > 0) {
      setNaturalSize({ width: image.naturalWidth, height: image.naturalHeight });
    }
  }, []);

  const handleOpenShowcase = useCallback(
    (showcase: SuperShowcase) => {
      setActiveId(showcase.id);
      setShowHotspots(false);
      setResultsHotspot(null);
      onShowcaseViewed?.(showcase);
    },
    [onShowcaseViewed],
  );

  // Share-link deep link: the app opens the dialog once the list has loaded,
  // so a missing id (unpublished / removed) simply leaves the gallery showing.
  useEffect(() => {
    if (!open || initialShowcaseId == null) return;
    const target = showcases.find((showcase) => showcase.id === initialShowcaseId);
    if (target) handleOpenShowcase(target);
    onInitialShowcaseHandled?.();
  }, [open, initialShowcaseId, showcases, handleOpenShowcase, onInitialShowcaseHandled]);

  const [shareTarget, setShareTarget] = useState<{ showcase: SuperShowcase; source: 'gallery' | 'viewer' } | null>(null);

  const handleHotspotClick = useCallback(
    (hotspot: SuperShowcaseHotspot) => {
      if (!active) return;

      if (hotspotAnimationTimerRef.current !== null) {
        window.clearTimeout(hotspotAnimationTimerRef.current);
        hotspotAnimationTimerRef.current = null;
      }

      const openResults = () => {
        // The app logs the click and handles the areas that open another dialog;
        // Project Research results are rendered here instead of navigating away.
        onHotspotAction(active, hotspot);
        if (hotspot.actionType === 'PROJECT_RESEARCH') {
          setResultsHotspot(hotspot);
        }
        setAnimatingHotspotId(null);
        hotspotAnimationTimerRef.current = null;
      };

      const opensTable = hotspot.actionType === 'PROJECT_RESEARCH'
        || hotspot.actionType === 'REGION_MARKET_DATA';
      if (isMobile && opensTable) {
        setAnimatingHotspotId(hotspot.id);
        setHoveredHotspotId(hotspot.id);
        hotspotAnimationTimerRef.current = window.setTimeout(openResults, 1000);
        return;
      }

      openResults();
    },
    [active, isMobile, onHotspotAction],
  );

  // ── Gallery ───────────────────────────────────────────────────────────

  const renderGallery = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 10 }}>
          <CircularProgress sx={{ color: 'var(--color-accent)' }} />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" action={<Button onClick={onRefresh}>Retry</Button>}>
            {error}
          </Alert>
        </Box>
      );
    }
    if (showcases.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center', color: 'var(--color-text-muted)' }}>
          <AutoAwesomeIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1 }} />
          <Typography variant="h6" sx={{ color: 'var(--color-text-strong)' }}>
            Nothing on show yet
          </Typography>
          <Typography variant="body2">New showcases appear here the moment they are published.</Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
          },
          p: { xs: 2, sm: 3 },
        }}
      >
        {showcases.map((showcase, index) => (
          <Box
            key={showcase.id}
            onClick={() => handleOpenShowcase(showcase)}
            sx={{
              position: 'relative',
              borderRadius: 3,
              overflow: 'hidden',
              cursor: 'pointer',
              bgcolor: 'var(--color-surface)',
              border: '1px solid rgba(var(--color-text-rgb), 0.1)',
              boxShadow: '0 8px 24px rgba(var(--color-shadow-rgb), 0.25)',
              transform: 'translateY(0)',
              opacity: 0,
              animation: `showcaseCardIn 420ms ease ${index * 60}ms forwards`,
              transition: 'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease',
              '@keyframes showcaseCardIn': {
                from: { opacity: 0, transform: 'translateY(14px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
              '&:hover': {
                transform: 'translateY(-6px)',
                borderColor: 'var(--color-accent)',
                boxShadow: '0 16px 36px rgba(var(--color-shadow-rgb), 0.4)',
                '& .showcase-card-cta': { opacity: 1, transform: 'translateY(0)' },
                '& .showcase-card-image': { transform: 'scale(1.04)' },
              },
            }}
          >
            <Box sx={{ position: 'relative', overflow: 'hidden', bgcolor: 'rgba(var(--color-text-rgb), 0.06)' }}>
              <Box
                className="showcase-card-image"
                component="img"
                src={showcase.imageUrl}
                alt={showcase.title}
                loading="lazy"
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  transition: 'transform 420ms ease',
                }}
              />
              {/* Hotspot preview frames so the card hints at what is clickable.
                  Held still here — a grid of trailing lights would be noise. */}
              {showcase.hotspots.map((hotspot) => {
                const freehand = hotspot.shape === 'freehand' && normalizePathRings(hotspot.pathData).length > 0;
                const bounds = freehand ? freehandBounds(hotspot.pathData) : null;
                return (
                <Box
                  key={hotspot.id}
                  sx={{
                    position: 'absolute',
                    left: `${hotspot.x ?? bounds?.x ?? 0}%`,
                    top: `${hotspot.y ?? bounds?.y ?? 0}%`,
                    width: `${hotspot.width ?? bounds?.width ?? 0}%`,
                    height: `${hotspot.height ?? bounds?.height ?? 0}%`,
                    clipPath: undefined,
                    borderRadius: freehand ? 0 : hotspot.shape === 'ellipse' ? '50%' : '8px',
                    background: freehand ? 'transparent' : `linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.02) 22%, ${getHotspotColor(hotspot)}00 46%, ${getHotspotColor(hotspot)}24 100%)`,
                    boxShadow: freehand ? 'none' : `inset 0 1px 0 rgba(255,255,255,0.34), inset 0 -1px 0 ${getHotspotColor(hotspot)}3D`,
                    pointerEvents: 'none',
                    opacity: 0.9,
                  }}
                >
                  <HotspotFrame color={getHotspotColor(hotspot)} shape={hotspot.shape} pathData={hotspot.pathData} live={false} />
                </Box>
                );
              })}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.78) 100%)',
                  pointerEvents: 'none',
                }}
              />
              <Chip
                size="small"
                icon={<TouchAppIcon sx={{ fontSize: 15 }} />}
                label={`${showcase.hotspots.length} interactive area${showcase.hotspots.length === 1 ? '' : 's'}`}
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 10,
                  bgcolor: 'rgba(0,0,0,0.6)',
                  color: '#fff',
                  fontWeight: 600,
                  backdropFilter: 'blur(6px)',
                  '& .MuiChip-icon': { color: 'var(--color-accent)' },
                }}
              />
              <Box
                className="showcase-card-cta"
                sx={{
                  position: 'absolute',
                  right: 12,
                  bottom: 12,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 999,
                  bgcolor: 'var(--color-accent)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  opacity: 0,
                  transform: 'translateY(8px)',
                  transition: 'opacity 220ms ease, transform 220ms ease',
                }}
              >
                Explore →
              </Box>
            </Box>

            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', flex: 1, minWidth: 0 }}>
                  {showcase.title}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<ShareIcon sx={{ fontSize: 16 }} />}
                  onClick={(event) => {
                    event.stopPropagation();
                    setShareTarget({ showcase, source: 'gallery' });
                  }}
                  sx={{
                    flexShrink: 0,
                    borderRadius: 999,
                    px: 1.5,
                    bgcolor: 'var(--color-accent)',
                    color: '#fff',
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: 'var(--color-accent)', filter: 'brightness(1.1)' },
                  }}
                >
                  Share
                </Button>
              </Box>
              {showcase.description && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'var(--color-text-muted)',
                    mt: 0.25,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {showcase.description}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.25 }}>
                {showcase.hotspots.slice(0, 4).map((hotspot) => (
                  <Chip
                    key={hotspot.id}
                    size="small"
                    label={hotspot.label}
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: getHotspotColor(hotspot),
                      bgcolor: `${getHotspotColor(hotspot)}1F`,
                      border: `1px solid ${getHotspotColor(hotspot)}66`,
                    }}
                  />
                ))}
                {showcase.hotspots.length > 4 && (
                  <Chip size="small" variant="outlined" label={`+${showcase.hotspots.length - 4}`} sx={{ height: 22, fontSize: '0.7rem' }} />
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // ── Viewer ────────────────────────────────────────────────────────────

  const renderHotspot = (hotspot: SuperShowcaseHotspot, index: number) => {
    const color = getHotspotColor(hotspot);
    const hovered = hoveredHotspotId === hotspot.id;
    const animating = animatingHotspotId === hotspot.id;
    const activeLayer = hovered || animating;
    const visible = showHotspots || activeLayer;
    const freehand = hotspot.shape === 'freehand' && normalizePathRings(hotspot.pathData).length > 0;
    const bounds = freehand ? freehandBounds(hotspot.pathData) : null;
    const left = hotspot.x ?? bounds?.x ?? 0;
    const top = hotspot.y ?? bounds?.y ?? 0;
    const width = hotspot.width ?? bounds?.width ?? 0;
    const height = hotspot.height ?? bounds?.height ?? 0;
    return (
      <Box
        key={hotspot.id}
        onClick={(event) => {
          event.stopPropagation();
          handleHotspotClick(hotspot);
        }}
        onMouseEnter={() => setHoveredHotspotId(hotspot.id)}
        onMouseLeave={() => setHoveredHotspotId((current) => (current === hotspot.id ? null : current))}
        sx={{
          position: 'absolute',
          left: `${left}%`,
          top: `${top}%`,
          width: `${width}%`,
          height: `${height}%`,
          zIndex: activeLayer ? 30 : index + 1,
          clipPath: undefined,
          borderRadius: hotspot.shape === 'ellipse' ? '50%' : '8px',
          cursor: 'pointer',
          // Specular sheen only — no backdrop blur so the chart stays crisp on hover.
          background: hotspot.shape === 'freehand' && hotspot.pathData
            ? 'transparent'
            : hovered
              ? `linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.03) 22%, ${color}00 46%, ${color}3D 100%)`
              : `linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.02) 22%, ${color}00 46%, ${color}24 100%)`,
          // The inset lines are the lit top edge and shaded bottom edge that
          // read as the thickness of the glass — they carry the definition the
          // sheen no longer supplies.
          boxShadow: hotspot.shape === 'freehand' && hotspot.pathData
            ? 'none'
            : hovered
              ? `inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -1px 0 ${color}7A, 0 0 40px ${color}7A`
              : `inset 0 1px 0 rgba(255,255,255,0.34), inset 0 -1px 0 ${color}3D, 0 0 20px ${color}33`,
          // Hidden by default, but never inert: the area still takes the click,
          // and hovering it (or its legend entry) draws it in.
          opacity: visible ? 1 : 0,
          pointerEvents: freehand ? 'none' : 'auto',
          transform: revealed ? 'scale(1)' : 'scale(0.92)',
          transitionProperty: 'opacity, transform, background, box-shadow',
          transitionDuration: '260ms',
          transitionTimingFunction: 'ease',
          transitionDelay: revealed ? `${index * 70}ms` : '0ms',
          '&:hover .showcase-hotspot-label': { opacity: 1, transform: 'translate(-50%, -6px)' },
        }}
      >
        <HotspotFrame color={color} shape={hotspot.shape} pathData={hotspot.pathData} live={visible} hovered={hovered}
          onClick={freehand ? (event) => { event.stopPropagation(); handleHotspotClick(hotspot); } : undefined}
          onMouseEnter={freehand ? () => setHoveredHotspotId(hotspot.id) : undefined}
          onMouseLeave={freehand ? () => setHoveredHotspotId((current) => current === hotspot.id ? null : current) : undefined} />

        <Box
          className="showcase-hotspot-label"
          sx={{
            position: 'absolute',
            left: '50%',
            bottom: '100%',
            zIndex: 31,
            transform: activeLayer || (showHotspots && isMobile) ? 'translate(-50%, -6px)' : 'translate(-50%, 2px)',
            opacity: activeLayer || (showHotspots && isMobile) ? 1 : 0,
            transition: 'opacity 180ms ease, transform 180ms ease',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            px: 1,
            py: 0.35,
            borderRadius: 999,
            // Glass readout rather than a solid swatch — the colour survives as
            // the outline, the glow and the status dot.
            bgcolor: 'rgba(8, 14, 24, 0.9)',
            border: `1px solid ${color}`,
            backdropFilter: 'blur(8px)',
            color: '#eef6fa',
            fontSize: '0.68rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
            boxShadow: `0 0 16px ${color}66, 0 4px 12px rgba(0,0,0,0.45)`,
            pointerEvents: 'none',
            maxWidth: '60vw',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: color,
              flexShrink: 0,
              boxShadow: `0 0 8px 1px ${color}`,
              animation: 'sscHalo 1.6s ease-in-out infinite',
            }}
          />
          {hotspot.label}
        </Box>
      </Box>
    );
  };

  const renderViewer = () => {
    if (!active) return null;
    return (
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            position: 'relative',
            bgcolor: '#0b0d10',
            backgroundImage:
              'radial-gradient(circle at 50% 0%, rgba(var(--color-accent-rgb), 0.18), transparent 55%)',
          }}
        >
          <TransformWrapper
            ref={transformRef}
            initialScale={1}
            minScale={1}
            maxScale={5}
            centerOnInit
            limitToBounds
            centerZoomedOut
            doubleClick={{ disabled: false, step: 0.7 }}
            wheel={{ step: 0.06 }}
            pinch={{ step: 5 }}
          >
            <TransformComponent
              wrapperStyle={{ width: '100%', height: '100%' }}
              contentStyle={{ width: '100%', height: '100%' }}
            >
              <Box
                ref={viewportRef}
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: { xs: 1, sm: 2 },
                  boxSizing: 'border-box',
                }}
              >
                {/* Frame is contain-fit to the image so hotspot % match the
                    painted pixels (same model as admin — no letterboxing). */}
                <Box
                  sx={{
                    position: 'relative',
                    width: frameSize?.width ?? 'auto',
                    height: frameSize?.height ?? 'auto',
                    maxWidth: '100%',
                    maxHeight: '100%',
                  }}
                >
                  <Box
                    component="img"
                    src={active.imageUrl}
                    alt={active.title}
                    draggable={false}
                    onLoad={handleActiveImageLoad}
                    sx={{
                      display: 'block',
                      width: frameSize ? '100%' : 'auto',
                      height: frameSize ? '100%' : 'auto',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      userSelect: 'none',
                    }}
                  />
                  {frameSize ? active.hotspots.map(renderHotspot) : null}
                </Box>
              </Box>
            </TransformComponent>
          </TransformWrapper>

          {/* Zoom controls */}
          <Box
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              bgcolor: 'rgba(0,0,0,0.5)',
              borderRadius: 2,
              p: 0.5,
              backdropFilter: 'blur(6px)',
            }}
          >
            <Tooltip title="Zoom in" placement="left">
              <IconButton size="small" sx={{ color: '#fff' }} onClick={() => transformRef.current?.zoomIn()}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom out" placement="left">
              <IconButton size="small" sx={{ color: '#fff' }} onClick={() => transformRef.current?.zoomOut()}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset view" placement="left">
              <IconButton size="small" sx={{ color: '#fff' }} onClick={() => transformRef.current?.resetTransform()}>
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* First-time hint */}
          {active.hotspots.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                bottom: 16,
                transform: 'translateX(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.75,
                py: 0.75,
                borderRadius: 999,
                bgcolor: 'rgba(0,0,0,0.62)',
                color: '#fff',
                fontSize: '0.78rem',
                fontWeight: 600,
                backdropFilter: 'blur(8px)',
                opacity: hintVisible ? 1 : 0,
                transition: 'opacity 600ms ease',
                pointerEvents: 'none',
              }}
            >
              <TouchAppIcon fontSize="small" sx={{ color: 'var(--color-accent)' }} />
              {showHotspots
                ? 'Tap a glowing area to open its numbers'
                : `${active.hotspots.length} clickable area${active.hotspots.length === 1 ? '' : 's'} on this picture — tap one, or use the eye to reveal them`}
            </Box>
          )}
        </Box>

        {/* Legend — every mapped area, also clickable. */}
        {active.hotspots.length > 0 && (
          <Box
            sx={{
              flexShrink: 0,
              borderTop: '1px solid rgba(var(--color-text-rgb), 0.12)',
              bgcolor: 'var(--color-surface)',
              px: { xs: 1.5, sm: 2.5 },
              py: 1.5,
              display: 'flex',
              gap: 1,
              overflowX: 'auto',
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(var(--color-text-rgb), 0.2)',
                borderRadius: 6,
              },
            }}
          >
            {active.hotspots.map((hotspot) => {
              const color = getHotspotColor(hotspot);
              const hovered = hoveredHotspotId === hotspot.id;
              return (
                <Box
                  key={hotspot.id}
                  onMouseEnter={() => setHoveredHotspotId(hotspot.id)}
                  onMouseLeave={() => setHoveredHotspotId((current) => (current === hotspot.id ? null : current))}
                  onClick={() => handleHotspotClick(hotspot)}
                  sx={{
                    position: 'relative',
                    flexShrink: 0,
                    maxWidth: 320,
                    px: 1.5,
                    pt: 1,
                    pb: 1.25,
                    borderRadius: 2,
                    cursor: 'pointer',
                    // Boxless to match the hotspots: a lit underline carries the
                    // affordance, and the wash only fades up on hover.
                    border: 'none',
                    background: hovered
                      ? `linear-gradient(180deg, ${color}00 0%, ${color}1F 100%)`
                      : 'transparent',
                    transition: 'background 160ms ease, transform 160ms ease',
                    transform: hovered ? 'translateY(-2px)' : 'none',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 6,
                      right: 6,
                      bottom: 0,
                      height: 2,
                      borderRadius: 2,
                      backgroundImage: hovered
                        ? `linear-gradient(90deg, transparent 0%, ${color} 38%, #ffffff 50%, ${color} 62%, transparent 100%)`
                        : `linear-gradient(90deg, ${color}00 0%, ${color}59 50%, ${color}00 100%)`,
                      backgroundSize: hovered ? '55% 100%' : '100% 100%',
                      backgroundRepeat: 'no-repeat',
                      filter: hovered ? `drop-shadow(0 0 6px ${color})` : 'none',
                      animation: hovered ? 'sscLegendRail 1.4s linear infinite' : 'none',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }} noWrap>
                      {hotspot.label}
                    </Typography>
                    <Chip
                      size="small"
                      label={getShowcaseActionMeta(hotspot.actionType).label}
                      sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: `${color}22`, color }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block', mt: 0.25 }} noWrap>
                    {describeHotspotAction(hotspot)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // ~92% of the viewport on desktop: big enough for the results table,
      // still framed as a panel over the app. Phones stay full screen.
      maxWidth={false}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-bg)',
          display: 'flex',
          flexDirection: 'column',
          m: isMobile ? 0 : 2,
          width: isMobile ? '100%' : '92vw',
          maxWidth: isMobile ? '100%' : '92vw',
          height: isMobile ? '100%' : '92vh',
          maxHeight: isMobile ? '100%' : '92vh',
          borderRadius: isMobile ? 0 : 3,
          overflow: 'hidden',
        },
      }}
    >
      <style>{SHOWCASE_KEYFRAMES}</style>

      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1,
          px: { xs: 1.5, sm: 2.5 },
          py: 1.5,
          boxSizing: 'border-box',
          overflow: 'hidden',
          borderBottom: '1px solid rgba(var(--color-text-rgb), 0.12)',
          background:
            'linear-gradient(90deg, rgba(var(--color-accent-rgb), 0.16), rgba(var(--color-accent-rgb), 0) 60%)',
        }}
      >
        {active ? (
          <IconButton
            onClick={() => (resultsHotspot ? setResultsHotspot(null) : setActiveId(null))}
            sx={{ color: 'var(--color-text-strong)' }}
          >
            <ArrowBackIcon />
          </IconButton>
        ) : (
          <AutoAwesomeIcon sx={{ color: 'var(--color-accent)', ml: 0.5 }} />
        )}

        <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', lineHeight: 1.2 }} noWrap>
            {resultsHotspot ? resultsHotspot.label : active ? active.title : 'Super Showcase'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'var(--color-text-muted)',
              display: 'block',
              whiteSpace: { xs: 'normal', sm: 'nowrap' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              overflowWrap: 'anywhere',
              lineHeight: 1.35,
            }}
          >
            {resultsHotspot
              ? `${active?.title ?? 'Super Showcase'} · back arrow returns to the picture`
              : active
                ? active.description || 'Click a mapped area for its numbers — the eye reveals where they are'
                : 'Pick a picture, click what interests you — the numbers follow'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {active && (
            <Button
              variant="contained"
              startIcon={<ShareIcon />}
              onClick={() => setShareTarget({ showcase: active, source: 'viewer' })}
              sx={{
                mr: 0.5,
                borderRadius: 999,
                px: { xs: 1.5, sm: 2 },
                bgcolor: 'var(--color-accent)',
                color: '#fff',
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: '0 0 0 3px rgb(var(--color-accent-rgb) / 0.25)',
                '&:hover': { bgcolor: 'var(--color-accent)', filter: 'brightness(1.1)' },
              }}
            >
              Share
            </Button>
          )}
          {active && !resultsHotspot && active.hotspots.length > 0 && (
            <Tooltip title={showHotspots ? 'Hide clickable areas' : 'Show clickable areas'}>
              <IconButton onClick={() => setShowHotspots((value) => !value)} sx={{ color: 'var(--color-text-strong)' }}>
                {showHotspots ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </IconButton>
            </Tooltip>
          )}
          {!active && (
            <Tooltip title="Refresh">
              <IconButton onClick={onRefresh} sx={{ color: 'var(--color-text-strong)' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose} sx={{ color: 'var(--color-text-strong)' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {resultsHotspot ? (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pt: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
            {(() => {
              const criteria = summarizeProjectResearchFilters(
                projectResearchFilters ?? getProjectResearchConfig(resultsHotspot)?.filters,
              );
              return criteria.length === 0 ? (
                <Chip size="small" variant="outlined" label="All matching projects" />
              ) : (
                criteria.map((part) => (
                  <Chip
                    key={part}
                    size="small"
                    label={part}
                    sx={{
                      fontWeight: 600,
                      bgcolor: 'rgba(var(--color-accent-rgb), 0.15)',
                      color: 'var(--color-accent)',
                    }}
                  />
                ))
              );
            })()}
            {renderProjectResearchCriteria && (
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() => setEditingCriteria((value) => !value)}
                sx={{
                  ml: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  color: 'var(--color-accent)',
                }}
              >
                {editingCriteria ? <ExpandLessIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                {editingCriteria ? 'Hide criteria' : 'Edit'}
              </Link>
            )}
          </Box>
          {renderProjectResearchCriteria && (
            <Collapse in={editingCriteria} unmountOnExit>
              <Box sx={{ px: { xs: 1, sm: 2 }, pt: 2 }}>{renderProjectResearchCriteria()}</Box>
            </Collapse>
          )}
          <Box sx={{ px: { xs: 1, sm: 2 }, pt: 2 }}>{renderProjectResearchResults?.()}</Box>
        </Box>
      ) : active ? (
        renderViewer()
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{renderGallery()}</Box>
      )}

      <ShowcaseShareDialog
        showcase={shareTarget?.showcase ?? null}
        onClose={() => setShareTarget(null)}
        onShared={(showcase, method) => onShowcaseShared?.(showcase, method, shareTarget?.source ?? 'gallery')}
      />
    </Dialog>
  );
};

export default SuperShowcaseDialog;
